/**
 * Arousal Effects Browser Page
 * 
 * Standardized to match ScenariosPage/ArousalMechanicsPage pattern:
 * - CollectionContext for state management
 * - DatabaseHero for premium hero section
 * - Table view only (no cards)
 * - Sticky control bar with filter pills
 * - Infinite scroll support
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
    Search, AlertTriangle, Activity, Eye, Clock, ChevronRight
} from 'lucide-react';
import { useDevMode } from '../contexts/DevModeContext';
import { useCollection } from '../contexts/CollectionContext';
import DatabaseHero from '../components/DatabaseHero';
import Spinner from '../components/Spinner';

// Category colors for badges
const getCategoryColor = (category) => {
    switch (category?.toLowerCase()) {
        case 'physical': return 'text-rose-400 bg-rose-500/10';
        case 'emotional': return 'text-purple-400 bg-purple-500/10';
        case 'behavioral': return 'text-blue-400 bg-blue-500/10';
        case 'cognitive': return 'text-amber-400 bg-amber-500/10';
        default: return 'text-gray-400 bg-gray-500/10';
    }
};

function ArousalEffectsPage() {
    const { isDevMode } = useDevMode();
    const {
        effects, loadEffects, isEffectsLoaded, effectsLoading, effectsError,
        totalEffects
    } = useCollection();

    const [search, setSearch] = useState('');
    const [activeCategory, setActiveCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);

    // Initial Load
    useEffect(() => {
        if (!isEffectsLoaded) {
            loadEffects({}, 1);
        }
    }, [isEffectsLoaded, loadEffects]);

    // Handle Search/Filter
    const handleSearch = useCallback((newSearch, newCat) => {
        const params = {
            search: newSearch !== undefined ? newSearch : search,
            category: (newCat !== undefined ? newCat : activeCategory) === 'all' ? '' : (newCat !== undefined ? newCat : activeCategory)
        };
        loadEffects(params, 1, true).then(data => {
            setHasMore(1 < (data?.pagination?.totalPages || 1));
        });
        setCurrentPage(1);
    }, [search, activeCategory, loadEffects]);

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(() => {
            handleSearch();
        }, 300);
        return () => clearTimeout(timer);
    }, [search, activeCategory]);

    // Load More
    const loadMore = useCallback(async () => {
        if (!loadingMore && hasMore) {
            setLoadingMore(true);
            try {
                const params = { search, category: activeCategory === 'all' ? '' : activeCategory };
                const data = await loadEffects(params, currentPage + 1);
                setCurrentPage(p => p + 1);
                setHasMore(currentPage + 1 < (data?.pagination?.totalPages || 1));
            } finally {
                setLoadingMore(false);
            }
        }
    }, [loadingMore, hasMore, search, activeCategory, currentPage, loadEffects]);

    const handleScroll = useCallback((e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop - clientHeight < 200 && hasMore && !loadingMore && !effectsLoading) {
            loadMore();
        }
    }, [hasMore, loadingMore, effectsLoading, loadMore]);

    // Filter locally for immediate feedback
    const filteredEffects = effects.filter(e =>
        !search ||
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.summary?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-charcoal">
                {/* Sticky Control Bar */}
                <div className="flex-shrink-0 bg-charcoal-950/80 backdrop-blur-sm border-b border-wine-900/20 px-6 py-3">
                    <div className="max-w-[1800px] mx-auto flex items-center gap-4">
                        <div className="flex-1 relative group">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-cream/40 group-focus-within:text-wine-400 transition-colors" />
                            <input
                                type="text"
                                placeholder="Search effects..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className="w-full bg-dark-900 border border-wine-800/50 py-2.5 pl-10 pr-4 text-sm text-cream placeholder-cream/30 focus:outline-none focus:border-wine-500/70"
                            />
                        </div>
                    </div>
                </div>

                {/* Category Filters */}
                <div className="flex-shrink-0 bg-charcoal-900/30 border-b border-wine-900/10 px-6 py-3 overflow-x-auto">
                    <div className="max-w-[1800px] mx-auto flex items-center gap-2">
                        {['all', 'Physical', 'Emotional', 'Behavioral', 'Cognitive'].map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-1.5 text-[10px] font-bold uppercase tracking-widest transition-all ${activeCategory === cat ? 'bg-wine-500 text-cream shadow-lg shadow-wine-500/20' : 'bg-dark-800 text-cream/70 hover:text-cream border border-wine-800/50'}`}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto w-full" onScroll={handleScroll}>
                    <main className="max-w-[1800px] w-full mx-auto px-6 py-8">
                        {/* Hero Section */}
                        {!search && activeCategory === 'all' && currentPage === 1 && (
                            <DatabaseHero
                                icon={Activity}
                                label="Physical Responses"
                                title="Arousal Effects"
                                highlightWord="Effects"
                                description="Involuntary physical and emotional responses that occur during arousal. From subtle flushes to intense reactions."
                                stats={[
                                    { value: totalEffects, label: 'Effects' },
                                    { value: '4', label: 'Categories' }
                                ]}
                            />
                        )}

                        {/* Error State */}
                        {effectsError && (
                            <div className="glass-card p-6 text-center text-red-400">
                                <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p>{effectsError}</p>
                            </div>
                        )}

                        {/* Skeleton Loading State */}
                        {effectsLoading && effects.length === 0 && (
                            <div className="border border-dark-800 bg-dark-900/50 animate-fadeIn">
                                {Array.from({ length: 8 }).map((_, i) => (
                                    <div
                                        key={i}
                                        className="grid grid-cols-[minmax(250px,1fr)_100px_80px_80px] gap-2 py-3 px-4 border-b border-dark-800/50 animate-pulse"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-wine-900/20" />
                                            <div className="flex-1">
                                                <div className="h-4 bg-dark-700 w-2/3 mb-2" />
                                                <div className="h-3 bg-dark-800 w-1/2" />
                                            </div>
                                        </div>
                                        <div className="h-5 bg-dark-700 w-16 self-center" />
                                        <div className="h-4 bg-dark-800 w-12 self-center mx-auto" />
                                        <div className="h-4 bg-dark-800 w-12 self-center mx-auto" />
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Table View */}
                        {filteredEffects.length > 0 && (
                            <div className="border border-wine-800/40 bg-dark-900/80">
                                {/* Table Header */}
                                <div className="sticky top-0 z-20 bg-dark-950 border-b border-wine-800/50">
                                    <div className="grid grid-cols-[minmax(250px,1fr)_100px_80px_80px] gap-2 py-3 px-4 items-center">
                                        <div className="text-cream/60 font-bold text-[10px] uppercase tracking-wider">Effect</div>
                                        <div className="text-cream/60 font-bold text-[10px] uppercase tracking-wider">Category</div>
                                        <div className="text-center text-cream/60 font-bold text-[10px] uppercase tracking-wider">Onset</div>
                                        <div className="text-center text-cream/60 font-bold text-[10px] uppercase tracking-wider">Visibility</div>
                                    </div>
                                </div>

                                {/* Table Body */}
                                <div className="divide-y divide-dark-800/50">
                                    {filteredEffects.map((effect) => (
                                        <Link
                                            key={effect.id}
                                            to={`/effect/${effect.id}`}
                                            className="grid grid-cols-[minmax(250px,1fr)_100px_80px_80px] gap-2 py-3 px-4 hover:bg-dark-800/30 transition-colors items-center"
                                        >
                                            {/* Name + Summary */}
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className="w-8 h-8 rounded-lg bg-wine-700/20 flex items-center justify-center flex-shrink-0">
                                                    <Activity size={16} className="text-wine-400" />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-bold text-cream text-sm truncate">{effect.name}</div>
                                                    {effect.summary && (
                                                        <div className="text-cream/50 text-xs line-clamp-1">{effect.summary}</div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Category Badge */}
                                            <div className="flex items-center">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${getCategoryColor(effect.category)}`}>
                                                    {effect.category || '-'}
                                                </span>
                                            </div>

                                            {/* Onset */}
                                            <div className="text-center text-cream/50 text-sm">
                                                {effect.onset || '-'}
                                            </div>

                                            {/* Visibility */}
                                            <div className="text-center text-cream/50 text-sm">
                                                {effect.visibility ? `${effect.visibility}/10` : '-'}
                                            </div>
                                        </Link>
                                    ))}
                                </div>

                                {/* Loading More */}
                                {loadingMore && (
                                    <div className="py-4 text-center text-cream/40 flex items-center justify-center gap-2">
                                        <Spinner size="sm" />
                                        Loading more...
                                    </div>
                                )}

                                {/* All Loaded */}
                                {!hasMore && filteredEffects.length > 0 && (
                                    <div className="py-4 text-center text-cream/40 text-sm">
                                        All {totalEffects} effects loaded
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Empty State */}
                        {!effectsLoading && filteredEffects.length === 0 && (
                            <div className="text-center py-12">
                                <Activity className="w-12 h-12 text-cream/30 mx-auto mb-4" />
                                <p className="text-cream/50">No effects found matching your criteria</p>
                            </div>
                        )}
                    </main>
                </div>
            </div>
        </>
    );
}

export default ArousalEffectsPage;
